import React, { useEffect, useState } from "react";
import { ClockCircleOutlined, ReloadOutlined, BellOutlined, CheckOutlined, MenuOutlined, FileTextOutlined, MessageOutlined } from "@ant-design/icons";
import { Layout, Tooltip, message, Badge, Popover, List, Button, Empty, Spin } from "antd";
import { refreshUserData } from "store/slices/AuthSlice";
import { getUnreadNotificationCount, getNotifications, markNotificationRead, markAllNotificationsRead } from "store/actions/AuthActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSessionTimeout from "CustomHooks/useSessionTimeout";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Header } = Layout;

const Navbar = ({ onMenuClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { formattedTime, isWarning } = useSessionTimeout();

  // Notification state
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(refreshUserData()).unwrap();
      message.success("Data refreshed");
    } catch {
      message.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const formattedDate = now.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        day: "numeric",
      });
      const day = now.toLocaleDateString("en-US", { weekday: "short" });
      const time = now.toLocaleTimeString("en-GB", { hour12: false });
      setCurrentDateTime(`${formattedDate} | ${day} | ${time}`);
    }, 1000);

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  // Poll unread notification count every 30 seconds
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await getUnreadNotificationCount();
        setUnreadCount(data.count || 0);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    // New or read conversations in the inbox change the bell too; don't wait for the poll.
    window.addEventListener('communication:unread-changed', fetchCount);

    // Pause when tab hidden
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener('communication:unread-changed', fetchCount);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleNotifOpenChange = async (open) => {
    setNotifOpen(open);
    if (open) {
      setNotifLoading(true);
      try {
        const data = await getNotifications(1, 15);
        // Endpoint returns a flat array when unpaginated, or {results: [...]} when paginated
        const list = Array.isArray(data) ? data : (data?.results || []);
        setNotifications(list);
      } catch {}
      setNotifLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  // Notifications that point at a record open it on click.
  const notificationRoute = (item) => {
    if (item.notification_type === 'LEAD_REQUIREMENTS_SUBMITTED' && item.reference_id) {
      return `/leads/edit/${item.reference_id}`;
    }
    // Message notifications carry the conversation route from the backend.
    return item.link || null;
  };

  const handleNotificationClick = (item) => {
    if (!item.is_read) handleMarkRead(item.id);
    const route = notificationRoute(item);
    if (route) {
      setNotifOpen(false);
      navigate(route);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const notificationContent = (
    <div className="app-notif-panel" style={{ width: "min(360px, calc(100vw - 32px))", maxHeight: 450 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllRead} style={{ fontSize: 12, padding: 0 }}>
            Mark all read
          </Button>
        )}
      </div>
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {notifLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
        ) : notifications.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" style={{ padding: '20px 0' }} />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(item) => (
              <List.Item
                onClick={() => handleNotificationClick(item)}
                style={{
                  padding: '10px 12px',
                  cursor: item.is_read && !notificationRoute(item) ? 'default' : 'pointer',
                  backgroundColor: item.is_read ? 'transparent' : 'rgba(24, 144, 255, 0.04)',
                  borderBottom: '1px solid #f5f5f5',
                }}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      backgroundColor: item.notification_type === 'PAYMENT_LINK_PAID' ? '#52c41a'
                        : item.notification_type === 'LEAD_REQUIREMENTS_SUBMITTED' ? '#722ed1'
                          : item.notification_type === 'COMMUNICATION_MESSAGE' ? '#1677ff' : '#faad14',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {item.notification_type === 'PAYMENT_LINK_PAID'
                        ? <CheckOutlined style={{ color: '#fff', fontSize: 16 }} />
                        : item.notification_type === 'LEAD_REQUIREMENTS_SUBMITTED'
                          ? <FileTextOutlined style={{ color: '#fff', fontSize: 16 }} />
                          : item.notification_type === 'COMMUNICATION_MESSAGE'
                            ? <MessageOutlined style={{ color: '#fff', fontSize: 16 }} />
                            : <ClockCircleOutlined style={{ color: '#fff', fontSize: 16 }} />
                      }
                    </div>
                  }
                  title={<span style={{ fontSize: 13, fontWeight: item.is_read ? 400 : 600 }}>{item.title}</span>}
                  description={
                    <div>
                      <div style={{ fontSize: 12, color: '#666', lineHeight: '1.4' }}>{item.message}</div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{dayjs(item.created_at).fromNow()}</div>
                    </div>
                  }
                />
                {!item.is_read && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff', flexShrink: 0, marginLeft: 8 }} />
                )}
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );


  return (
    <>
      <Header
        className="site-layout-background app-header"
        style={{
          height: "50px",
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "0 10px",
        }}
      >
        {/* Opens the nav drawer; hidden by CSS once the Sider is visible. */}
        <Button
          type="text"
          className="app-nav-trigger"
          aria-label="Open navigation menu"
          onClick={onMenuClick}
          icon={<MenuOutlined style={{ color: "rgba(255,255,255,0.85)", fontSize: 18 }} />}
          style={{ marginRight: "auto" }}
        />

        <div style={{ display: "flex", alignItems: "center" }}>
          {/* <UserOutlined size={50} /> */}

          {/* <Dropdown
            style={{}}
            menu={{
              items,
            }}
            trigger={["click"]}
          >
            <a onClick={(e) => e.preventDefault()} style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                style={{
                  // border:"1px solid white",
                  marginRight: "9px",
                  backgroundColor: "#001529",
                  color: "#fff",
                }}
                icon={<UserOutlined style={{ color: "#fff" }} />}
              />
              <Space style={{ color: "#fff", marginTop: "0px" }}>
                User
                <DownOutlined size={30} style={{ color: "#fff" }} />
              </Space>
            </a>
          </Dropdown> */}
        </div>

        {/* Notifications Bell */}
        <Popover
          content={notificationContent}
          trigger="click"
          open={notifOpen}
          onOpenChange={handleNotifOpenChange}
          placement="bottomRight"
          arrow={false}
          overlayInnerStyle={{ padding: 0, borderRadius: 8 }}
        >
          <Tooltip title="Notifications">
            <div
              style={{
                marginRight: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)")}
            >
              <Badge count={unreadCount} size="small" offset={[2, -2]}>
                <BellOutlined style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "16px" }} />
              </Badge>
            </div>
          </Tooltip>
        </Popover>

        {/* Refresh Button */}
        <Tooltip title="Refresh permissions & user data">
          <div
            onClick={handleRefresh}
            style={{
              marginRight: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)")}
          >
            <ReloadOutlined
              spin={refreshing}
              style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "14px" }}
            />
          </div>
        </Tooltip>

        {/* Session Timeout Countdown */}
        <Tooltip title="Session expires due to inactivity. Any activity resets the timer.">
          <div
            className="app-header-session"
            style={{
              marginRight: "20px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 12px",
              borderRadius: "4px",
              backgroundColor: isWarning ? "rgba(255, 77, 79, 0.2)" : "rgba(255, 255, 255, 0.1)",
              transition: "background-color 0.3s ease",
            }}
          >
            <ClockCircleOutlined
              style={{
                color: isWarning ? "#ff4d4f" : "rgba(255, 255, 255, 0.65)",
                fontSize: "14px",
              }}
            />
            <span
              style={{
                color: isWarning ? "#ff4d4f" : "rgba(255, 255, 255, 0.85)",
                fontFamily: "monospace",
                fontSize: "13px",
                fontWeight: isWarning ? 600 : 400,
              }}
            >
              {formattedTime}
            </span>
          </div>
        </Tooltip>

        {/* Live Clock */}
        <div className="app-header-clock" style={{ marginRight: "20px", color: "#fff" }}>
          {currentDateTime}
        </div>
      </Header>
    </>
  );
};

export default Navbar;
